import angular from 'angular';
import _ from 'lodash-es';
import kformUtils from '~/components/kform/utils/kform-utils';
import kformBuilderUtils from '~/components/kform-builder/utils/kform-builder.utils';
import templateModalConfirmRemove from '../modal/confirm-remove/modal-kform-builder-confirm-remove.html';
import ModalKformBuilderConfirmRemoveController
	from '../modal/confirm-remove/modal-kform-builder-confirm-remove.controller';
import templateModalComponentSettings
	from '../modal/component-settings/modal-kform-builder-component-settings.html';
import ModalKformBuilderComponentSettingsController
	from '../modal/component-settings/modal-kform-builder-component-settings.controller';
import { createComponentInstance } from '~/components/kform/components/kform-component-class';

export default class KformBuilderListController {
	/* @ngInject */
	/**
	 * @param {any} $scope
	 * @param {import('angular').IRootElementService}$element
	 * @param {any} $rootScope
	 * @param {import('angular').ui.bootstrap.IModalService}$uibModal
	 * @param {any} KformComponents
	 * @param {any}dndDragIframeWorkaround
	 * @param {import('angular').ITimeoutService}$timeout
	 */
	constructor(
		$scope,
		$element,
		$rootScope,
		$uibModal,
		KformComponents,
		dndDragIframeWorkaround,
		$timeout,
	) {
		this.$scope = $scope;
		this.$element = $element;
		this.$rootScope = $rootScope;
		this.$uibModal = $uibModal;
		this.KformComponents = KformComponents;
		this.dndDragIframeWorkaround = dndDragIframeWorkaround;
		this.$timeout = $timeout;

		/** @type {Record<string, any>} */
		this.data = {};

		/** @type {import('~/scripts/services/model/form').default | undefined} */
		this.form = undefined;

		this.hideDndBoxCount = 1;
		this.options = {
			class_kid: undefined,
			noLockKeys: false,
		};
	}

	/**
	 * @param {any} settings
	 * @param {number | undefined} index
	 * @param {any | undefined} _
	 * @param {Function | undefined} callback
	 * @returns {boolean}
	 */
	addComponent(settings, index = undefined, _ = undefined, callback = undefined) {
		let component;
		if (typeof callback === 'function') {
			const builderComponent = callback();
			if (builderComponent) {
				component = createComponentInstance(callback().settings);
				if (component && component.isNew && !component.label) {
					component.label = builderComponent.title;
				}
			}
		}
		else {
			component = createComponentInstance(settings)
		}

		if (!component) {
			return false;
		}

		// Allow changing default lock options.
		if (this.options?.noLockKeys) {
			delete component.lockKey;
		}

		// Add parent key to default key if parent is present.
		// Sometimes this.component is the parent but columns and tables it is actually the column.
		const parent = this.$scope.parent ?? this.component;
		if (
			parent.type !== 'form'
			&& parent.type !== 'resource'
			&& component.isNew
			&& component.key
			&& parent.key !== undefined
		) {
			this.parentKey = parent.key;
			component.key = this.parentKey + _.upperFirst(component.key);
		}
		else {
			this.parentKey = '';
		}

		if (index === undefined) {
			// eslint-disable-next-line no-param-reassign
			index = -1;
		}

		// Only edit immediately for components that are not resource comps.
		if (component.isNew && (!component.key || !component.key.includes('.'))) {
			(async() => this.editComponent(component, index))();
		}
		else {
			// Ensure the component has a key.
			component.key = component.key || component.label || 'component';

			kformBuilderUtils.uniquify(this.form, component);

			// Update the component to not be flagged as new anymore.
			kformUtils.eachComponent([component], (/** @type {any} */ child) => {
				delete child.isNew;
			}, true);
		}

		this.dndDragIframeWorkaround.isDragging = false;
		this.$scope.emit('add');

		// Make sure that they don't ever add a component on the bottom of the submit button.
		const lastComponent = this.component.components.at(-1);
		if (
			(lastComponent)
			&& (lastComponent.type === 'button')
			&& (lastComponent.action === 'submit')
		) {
			// There is only one element on the page.
			if (this.component.components.length === 1) {
				// eslint-disable-next-line no-param-reassign
				index = 0;
			}
			else if (index !== undefined && index >= this.component.components.length) {
				// eslint-disable-next-line no-param-reassign
				index -= 1;
			}
		}

		if (index === undefined) {
			// eslint-disable-next-line no-param-reassign
			index = this.component.components.length;
		}

		// Fix path
		if ('kdata' in component && 'path' in component.kdata && component.kdata?.path?.length === 0) {
			component.kdata.path.push({
				kid:        this.options.class_kid,
				linker_kid: '0',
			});
		}

		// Add the component to the components array.
		this.component.components.splice(index, 0, component);
		this.$timeout(this.$scope.$apply.bind(this.$scope));

		// Return true since this will tell the drag-and-drop list component to not insert into its own array.
		return true;
	}

	// Clone form component
	cloneComponent(/** @type {any} */ component) {
		const newComponent = angular.copy(component);

		// If we are in a panel, the cloned component container should be the parent.components,
		// Otherwise it should be the form.components.
		let container;
		if (this.$scope.$parent.component && this.$scope.$parent.component.type === 'columns') {
			const column = _.find(
				this.$scope.$parent.component.columns,
				column2 => _.find(column2.components, { key: newComponent.key }),
			);
			container = column.components;
		}
		else if (this.$scope.$parent.component) {
			container = this.$scope.$parent.component.components;
		}
		else {
			container = this.form?.components;
		}

		// Add the new component right after the original.
		const index = container.indexOf(component);
		container.splice(index + 1, 0, newComponent);

		// timeout: wait for the form to have the new component.
		this.$timeout(() => {
			kformUtils.eachComponent([newComponent], (/** @type {any} */ child) => {
				child.isNew = true;
				kformBuilderUtils.uniquify(this.form, child);
				delete child.isNew;
			}, true);
		});
	}

	/**
	 * @param {any} component
	 */
	copyComponent(component) {
		window.sessionStorage.setItem('componentClipboard', JSON.stringify(angular.copy(component)));
	}

	/**
	 * Edit a specific component.
	 * @param {any} component
	 * @param {number} index
	 * @returns {Promise<void>}
	 */
	async editComponent(component, index = 0) {
		this.formComponent = this.KformComponents.components[component.type] || this.KformComponents.components.custom;
		// No edit view available
		if (!Object.prototype.hasOwnProperty.call(this.formComponent, 'views')) {
			return;
		}

		const previousSettings = angular.copy(component);

		// Make sure this component has a key.
		if (!component.key) {
			component.key = component.type;
		}
		this.updateKey(component);

		/** @type {any} */
		const resolve = {
			classKid:  () => this.options.class_kid,
			parent:    () => this,
			component: () => component,
			data:      () => ({
				[component.key]: [],
			}),
			form:      this.form,
			index,
		};

		// Open the dialog.
		const originalKey = component.key;
		try {
			const save = await this.$uibModal.open({
				ariaLabelledBy:  'modal-kform-builder-component-settings-title',
				ariaDescribedBy: 'modal-kform-builder-component-settings-body',
				template:        templateModalComponentSettings,
				controller:      ModalKformBuilderComponentSettingsController,
				resolve,
				size:            'full-with-footer',
			}).result;

			if (save) {
				// If there is no component label, then set it to the key and set hide label to ensure reverse compatibility.
				// Don't calculate for components that don't have a label.
				if (!component.label && ![
					'panel',
					'content',
					'fieldset',
					'table',
					'well',
					'columns',
				].includes(component.type)) {
					component.key = originalKey;
					component.label = component.key || component.type;
					component.hideLabel = true;
				}

				kformUtils.eachComponent([component], (/** @type {any} */ child) => {
					delete child.isNew;
				}, true);

				this.$scope.emit('edit', component);
			}
			else {
				if (component.isNew) {
					this.remove(component);
				}

				// Revert to old settings, but use the same object reference
				_.assign(component, previousSettings);
			}
		}
		catch (error) {
			if (error !== 'backdrop click' && error !== 'escape key press') {
				console.error(error);
			}
		}
	}

	pasteComponent() {
		let component;
		try {
			component = JSON.parse(window.sessionStorage.getItem('componentClipboard') || '');
		}
		catch {
			console.error('Error fetching componentClipboard');
		}

		if (component) {
			this.cloneComponent(component);
		}
	}

	remove(/** @type {any} */ component) {
		if (this.component.components.includes(component)) {
			this.component.components.splice(this.component.components.indexOf(component), 1);
			this.$scope.emit('remove', component);
		}
	}

	/**
	 * @param {any} component
	 * @param {boolean} shouldConfirm
	 */
	removeComponent(component, shouldConfirm) {
		if (shouldConfirm) {
			this.$uibModal.open({
				ariaLabelledBy:  'modal-kform-builder-confirm-remove-title',
				ariaDescribedBy: 'modal-kform-builder-confirm-remove-body',
				template:        templateModalConfirmRemove,
				controller:      ModalKformBuilderConfirmRemoveController,
				controllerAs:    '$ctrl',
				size:            'sm',
				backdrop:        'static',
			}).result.then(confirm => {
				if (confirm) {
					this.remove(component);
				}
				return true;
			}).catch(() => {});
		}
		else {
			this.remove(component);
		}
	}

	/**
	 * @param {any} component
	 */
	saveComponent(component) {
		this.$scope.emit('update', component);
		// ngDialog.closeAll(true);
	}

	/**
	 * @param {any} component
	 */
	updateKey(component) {
		if (!component.lockKey && component.isNew) {
			if (Object.prototype.hasOwnProperty.call(this.data, component.key)) {
				delete this.data[component.key];
			}
			if (component.label) {
				const invalidRegex = /^[^A-Z_a-z]*|[^\w-]*/g;
				component.key = _.camelCase(`${this.parentKey} ${component.label.replaceAll(invalidRegex, '')}`);
			}
			kformBuilderUtils.uniquify(this.form, component);
			this.data[component.key] = component.multiple ? [''] : '';
		}
	}

	$onInit() {
		this.builder = true;
		this.$rootScope.builder = true;
		this.hideCount = (typeof this.hideDndBoxCount === 'number' ? this.hideDndBoxCount : 1);
		this.$scope.$watch('$ctrl.hideDndBoxCount', (/** @type {number} */ hideCount) => {
			this.hideCount = hideCount || 1;
		});

		this.formComponents = this.KformComponents.components;
		if (!this.component) {
			this.component = this.form;
			this.$scope.$watch('$ctrl.form', (/** @type {any} */ newForm) => {
				if (this.component?.type === 'form') {
					this.component = newForm;
				}
			});
		}

		// Components depend on this existing
		if (!this.data) {
			this.data = {};
		}

		this.$scope.emit = (/** @type {any[]} */ ...arguments__) => {
			/**
			 * @type {any[]}
			 */
			const arguments2 = [].slice.call(arguments__);
			arguments2[0] = `formBuilder:${arguments2[0]}`;
			// eslint-disable-next-line prefer-spread
			this.$scope.$emit.apply(this.$scope, arguments2);
		};

		this.$scope.$on('editComponent', async (/** @type {Event} */ event, /** @type {any} */ component) => {
			await this.editComponent(component);
		});

		this.$scope.$on('fbDragDrop', (/** @type {Event} */ event, /** @type {any} */ component) => {
			component.settings.id = Math.random().toString(36).slice(7);
			component.settings.overlay = {
				page: '1',
				top:  component.fbDropY,
				left: component.fbDropX,
			};
			this.addComponent(component.settings);
		});

		this.$scope.$on('updateKey', (/** @type {any} */ component) => this.$scope.updateKey(component));
	}
}
